import {
    ROW_HEIGHT,
    BREAKPOINTS,
    LAYOUT,
    TYPOGRAPHY,
    FILLING_TYPOGRAPHY
} from './constants';

/**
 * Розраховує ефективну середню ширину символу з урахуванням типу тексту
 * @param {string} text - текст для аналізу
 * @param {number} baseCharWidth - базова ширина символу
 * @returns {number} ефективна середня ширина символу
 */
const getEffectiveCharWidth = (text, baseCharWidth) => {
    if (!text || text.length === 0) return baseCharWidth;

    const latinAndDigits = (text.match(/[a-zA-Z0-9]/g) || []).length;
    const punctuation = (text.match(/[,.\s\-:;()\[\]\/]/g) || []).length;
    const totalChars = text.length;

    const narrowRatio = (latinAndDigits + punctuation) / totalChars;
    const widthMultiplier = 1 - (narrowRatio * 0.18);

    return baseCharWidth * Math.max(widthMultiplier, 0.75);
};

/**
 * Розраховує додаткову висоту для складу комплексного дослідження
 * @param {Object} item - елемент каталогу
 * @param {number} width - ширина контейнера
 * @returns {number} додаткова висота в пікселях
 */
const getComplexFillingHeight = (item, width) => {
    if (item.type !== 'complex') return 0;

    const fillings = [...(item.filling || []), ...(item.idGrTest || [])];
    if (fillings.length === 0) return 0;

    let charsPerLine;
    if (width <= BREAKPOINTS.SMALL_MOBILE) {
        charsPerLine = FILLING_TYPOGRAPHY.CHARS_PER_LINE_MOBILE;
    } else if (width <= BREAKPOINTS.MOBILE) {
        charsPerLine = FILLING_TYPOGRAPHY.CHARS_PER_LINE_TABLET;
    } else {
        charsPerLine = FILLING_TYPOGRAPHY.CHARS_PER_LINE_DESKTOP;
    }

    const totalLines = fillings.reduce((acc, fill) => {
        const nameLength = fill?.name?.length || 0;
        if (nameLength > charsPerLine) {
            return acc + Math.ceil(nameLength / charsPerLine);
        }
        return acc + 1;
    }, 0);

    return totalLines * FILLING_TYPOGRAPHY.LINE_HEIGHT;
};

/**
 * Розраховує висоту рядка для віртуалізованого списку
 * @param {Object} item - елемент аналізу
 * @param {number} width - ширина контейнера
 * @param {number} takeId - ID типу забору
 * @returns {number} висота в пікселях
 */
export const getAssayRowHeight = (item, width, takeId) => {
    const name = item.name || '';

    // Заголовки груп мають фіксовану висоту
    if (item.superGroupName) return ROW_HEIGHT.GROUP_HEADER;

    if (!width || width === 0) {
        return ROW_HEIGHT.MIN;
    }

    const isMobile = width < BREAKPOINTS.MOBILE;
    const isSmallMobile = width <= BREAKPOINTS.SMALL_MOBILE;

    let baseCharWidth, lineHeight, fixedColumnsWidth;

    if (isSmallMobile) {
        baseCharWidth = TYPOGRAPHY.AVG_CHAR_WIDTH_MOBILE;
        lineHeight = TYPOGRAPHY.LINE_HEIGHT_MOBILE;
        fixedColumnsWidth = LAYOUT.FIXED_COLUMNS_WIDTH_MOBILE;
    } else if (isMobile) {
        baseCharWidth = TYPOGRAPHY.AVG_CHAR_WIDTH_TABLET;
        lineHeight = TYPOGRAPHY.LINE_HEIGHT_DESKTOP;
        fixedColumnsWidth = LAYOUT.FIXED_COLUMNS_WIDTH_MOBILE;
    } else {
        baseCharWidth = TYPOGRAPHY.AVG_CHAR_WIDTH_DESKTOP;
        lineHeight = TYPOGRAPHY.LINE_HEIGHT_DESKTOP;
        fixedColumnsWidth = LAYOUT.FIXED_COLUMNS_WIDTH_DESKTOP;
    }

    const avgCharWidth = getEffectiveCharWidth(name, baseCharWidth);

    const scrollbarWidth = isMobile ? 0 : LAYOUT.SCROLLBAR_WIDTH;
    const nameColumnWidth = Math.max(
        width - fixedColumnsWidth - scrollbarWidth - LAYOUT.CELL_PADDING,
        100
    );

    const textWidth = name.length * avgCharWidth * TYPOGRAPHY.WORD_WRAP_BUFFER;
    const nameLines = Math.ceil(textWidth / nameColumnWidth);

    const basePadding = 10;
    const nameHeight = Math.max(nameLines, 1) * lineHeight + basePadding;

    let extraBuffer = 0;
    if (nameLines >= 25) {
        extraBuffer = 28;
    } else if (nameLines >= 20) {
        extraBuffer = 24;
    } else if (nameLines >= 12) {
        extraBuffer = 18;
    } else if (nameLines >= 8) {
        extraBuffer = 16;
    } else if (nameLines >= 5) {
        extraBuffer = 14;
    } else if (nameLines >= 3) {
        extraBuffer = 8;
    } else if (nameLines >= 2) {
        extraBuffer = 4;
    }

    const calculatedHeight = nameHeight + extraBuffer;
    const fillingHeight = getComplexFillingHeight(item, width);

    return Math.max(calculatedHeight + fillingHeight, ROW_HEIGHT.MIN);
};
